import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCartPricing } from '../contexts/CartPricingContext';
import { useLocale } from '../contexts/LocaleContext';
import CheckoutContext from '../lib/CheckoutContext';
import { getFreeTrialCartItemIds } from '../lib/cart';
import { isAmountGiftCardSku } from '../lib/hardwareFirstGiftPlans';
import { getProductsAndVariantsBySku } from '../lib/product';
import { getLocalizedPricing } from '../lib/util/money';
import * as types from '../types';

/**
 * List price of the hardware-first cart's lines that are neither repriced trackers nor trial units, e.g. a band,
 * with amount gift cards split out for their own row. The server charges them at catalog price inside its total,
 * so the receipt has to list them for its rows to add up.
 */
export default function useHardwareFirstAccessoriesInCents(): { accessoriesInCents: number; giftCardInCents: number } {
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  const { hardwareDiscounts } = useCartPricing();
  const { currency } = useLocale();

  return useMemo(() => {
    const skuMap = getProductsAndVariantsBySku(products);
    const trialIds = new Set(getFreeTrialCartItemIds(cart, products));
    return Object.values(cart.cartItems)
      .filter((item) => !hardwareDiscounts?.[item.cartItemId] && !trialIds.has(item.cartItemId))
      .reduce(
        (sums, item) => {
          const product = skuMap.get(item.lineItem.sku)?.product;
          if (!product) {
            return sums;
          }
          const key = isAmountGiftCardSku(item.lineItem.sku) ? 'giftCardInCents' : 'accessoriesInCents';
          return { ...sums, [key]: sums[key] + getLocalizedPricing(product.pricingInCents, currency) * item.quantity };
        },
        { accessoriesInCents: 0, giftCardInCents: 0 },
      );
  }, [cart, products, hardwareDiscounts, currency]);
}
